import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Server, MessageSquare, Activity, BarChart3, Inbox } from 'lucide-react';

import { MailSpoolView } from './components/MailSpoolView';
import { OutgoingQueueView } from './components/OutgoingQueueView';
import { AWSStatusView } from './components/AWSStatusView';
import { EmailStatsView } from './components/EmailStatsView';
import { MessageThreadsView } from './components/MessageThreadsView';
import { SupportInboundQueueView } from './components/SupportInboundQueueView';

export const AdminEmailManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState('spool');

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Mail className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Email System Administration</h1>
          <p className="text-gray-600 mt-1">Manage mail spool, support inbound queue, outgoing queue, AWS status, and secure messages</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="spool" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Mail Spool
          </TabsTrigger>
          <TabsTrigger value="support-inbound" className="flex items-center gap-2">
            <Inbox className="h-4 w-4" />
            Support Inbound
          </TabsTrigger>
          <TabsTrigger value="queue" className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            Outgoing Queue
          </TabsTrigger>
          <TabsTrigger value="aws" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            AWS Status
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Email Stats
          </TabsTrigger>
          <TabsTrigger value="threads" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Secure Messages
          </TabsTrigger>
        </TabsList>

        <TabsContent value="spool" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Incoming Mail Spool
              </CardTitle>
              <p className="text-sm text-gray-600">
                Messages received from external systems waiting to be processed
              </p>
            </CardHeader>
            <CardContent>
              <MailSpoolView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="support-inbound" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Inbox className="h-5 w-5" />
                Support Inbound Mail Queue
              </CardTitle>
              <p className="text-sm text-gray-600">
                Review inbound support email records from diversion_support and open S3 message previews
              </p>
            </CardHeader>
            <CardContent>
              <SupportInboundQueueView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="queue" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                Outgoing Email Queue
              </CardTitle>
              <p className="text-sm text-gray-600">
                Emails queued for delivery through AWS SES with retry management
              </p>
            </CardHeader>
            <CardContent>
              <OutgoingQueueView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="aws" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                AWS SES Status
              </CardTitle>
              <p className="text-sm text-gray-600">
                Current AWS SES limits, reputation, and sending statistics
              </p>
            </CardHeader>
            <CardContent>
              <AWSStatusView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stats" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Email Statistics & Spam Monitoring
              </CardTitle>
              <p className="text-sm text-gray-600">
                Subject analysis, sending patterns, and spam detection for today's emails
              </p>
            </CardHeader>
            <CardContent>
              <EmailStatsView />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="threads" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Secure Message Threads
              </CardTitle>
              <p className="text-sm text-gray-600">
                Secure messaging system threads and conversations
              </p>
            </CardHeader>
            <CardContent>
              <MessageThreadsView />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
